import { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { UserProfileUtils } from 'src/context/userProfileContext'
import { useTrackEvents } from 'src/context/userProfileContext/hooks'
import { ComponentName } from 'src/utils/datahubDefaultEvent'
import { UserAvatar } from 'src/components/userAvatar'
import { DateUtils } from 'src/utils/date'
import { useMentionUtils } from 'src/components/postCommentInput/hooks'
import { ComponentNames } from 'src/utils/datahub/enums'
import { ModerationTag } from '../moderationTag'
import { CommentProps, MediaPreview } from '../postComments/components'
import { FirstCommentBadge } from '../firstCommentBadge'
import { useUserProfile } from '../globalUserProfile'
import { PostCommentProps } from './postComment.types'
import { AuthorName, CommentContent, CommentText, ModerationWrapper } from './postComment.styles'

const PostComment = ({ comment, postId, className }: PostCommentProps) => {
  const container = useRef<HTMLDivElement>(null)
  const { loggedUserUcode, onOpen: onOpenUserProfile } = useUserProfile()
  const { sendOpenProfileEvent } = useTrackEvents({ loggedUserUcode })
  const { convertMentionToHTML } = useMentionUtils()

  const isModerator = comment?.authorMemberType === 'MODERATOR'
  const canOpenUserProfile = Boolean(comment?.authorUcode)
  const commentDate = DateUtils.compareDates(comment.date as number)

  const convertCommentToHTML = (comment: CommentProps) => {
    return convertMentionToHTML({
      text: comment?.message,
      mentions: comment?.mentions?.map((mention) => ({
        tag: mention?.tag,
        failed: Boolean(mention?.failed),
        failureReason: mention?.failureReason,
        ucode: mention?.metadata?.ucode,
        name: mention?.metadata?.name
      }))
    })
  }

  const handleOpenUserProfile = () => {
    if (canOpenUserProfile) {
      // onOpenUserProfile(event, comment.authorUcode)
      onOpenUserProfile(comment.authorUcode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_COMMENT,
        userUcode: comment.authorUcode
      })
    }
  }

  const profileActionProps = canOpenUserProfile && {
    role: 'button',
    tabIndex: 0,
    onClick: handleOpenUserProfile,
    onKeyDown: handleOpenUserProfile
  }

  useEffect(() => {
    const elementsToOpenProfile = container.current?.querySelectorAll(
      '[data-action="open-profile"]'
    )

    const callback = ({ ucode }) => {
      onOpenUserProfile(ucode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_COMMENT,
        userUcode: ucode,
        accessFromMention: true
      })
    }

    const removeListener = UserProfileUtils.openEventListener({
      elements: elementsToOpenProfile,
      componentName: ComponentName.POST_COMMENT,
      callback
    })

    return removeListener
  }, [comment, postId])

  return (
    <div
      ref={container}
      className={clsx(className, '_d-flex _align-items-start _w-full _flex-column')}
    >
      <div className="_d-flex _align-items-center _w-full _pb-4">
        <div
          className={clsx('_mr-3', {
            '_cursor-pointer': canOpenUserProfile
          })}
          {...profileActionProps}
        >
          <UserAvatar src={comment?.authorImageFinalUrl} alt={comment.author} small />
        </div>

        <div className="_d-flex _flex-column _flex-1">
          <div className="_d-flex _align-items-center">
            <AuthorName
              className={clsx('_text-2 _text-gray-900 _font-weight-bold _line-height _mr-2', {
                '_cursor-pointer': canOpenUserProfile
              })}
              {...profileActionProps}
            >
              {comment.author}
            </AuthorName>

            {isModerator && (
              <ModerationWrapper className="_pr-3 _mr-2">
                <ModerationTag />
              </ModerationWrapper>
            )}

            <span className="_text-1 _text-gray-600">{commentDate}</span>
          </div>
        </div>
      </div>

      <CommentContent>
        {comment?.first && <FirstCommentBadge className="_mb-2" />}

        {comment?.message && (
          <CommentText
            className="_font-14 _mb-0"
            dangerouslySetInnerHTML={{ __html: convertCommentToHTML(comment) }}
          />
        )}

        <MediaPreview
          postId={postId}
          comment={comment}
          arrivedFrom={ComponentNames.POST_FEED_COMMENT}
        />
      </CommentContent>
    </div>
  )
}

export default PostComment
